import { create } from 'zustand';
import type { DirectoryKiosk } from '../hooks/useDirectoryKiosks';
import type { Venue } from '../lib/map-helpers';
import type {
  MappedinFloor,
  MappedinLocation,
  MappedinMapData,
  MappedinMapView,
  MappedinSpace,
} from '../lib/mappedin-types';
import { storage } from '../lib/storage';

/**
 * Venue Overview's core store. Mirrors `src/state.js` from the App
 * Builder export, converted to a typed zustand store. Runtime escape-
 * hatches that used `window._foo` in the original (hidden-space set,
 * underground colors, dynamic-space set) now live on the store.
 *
 * Note on Mappedin mutation: this store intentionally holds the live
 * `MapView` + `MapData` references so the stacking and layer code can
 * call `mapView.updateState(...)` / `animateState(...)` directly. Those
 * methods drive per-floor altitude / opacity / marker animations that
 * can't easily be expressed as pure React state.
 */

export const FACADE_GAP = 4;
const BSMT_SHALLOW = 0.35;
const BSMT_DEEP = 0.08;
export const UNDERGROUND_COLOR = '#2d3f52';

const CFG_KEY = 'map-config';

export interface MapCameraTransition {
  duration?: number;
}

export type AppMode = 'globe' | 'venue';

export interface AppState {
  appMode: AppMode;
  venues: Venue[];
  venuesLoading: boolean;
  selectedVenue: Venue | null;
  hoveredBinKey: string | null;
  showVenueMap: boolean;
  globeSearchQuery: string;

  mapView: MappedinMapView | null;
  mapData: MappedinMapData | null;
  mapLoading: boolean;
  mapError: string | null;

  floors: MappedinFloor[];
  outdoorFloors: MappedinFloor[];
  activeFloor: MappedinFloor | null;
  isStacked: boolean;
  floorGap: number;
  isMultiBuilding: boolean;
  showingFacades: boolean;
  animPreview: number | null;

  pitch: number;
  bearing: number;
  autoBearing: number;

  labelsEnabled: boolean;
  inactiveOpacity: number;
  darkBackground: boolean;
  showBounds: boolean;

  locations: MappedinLocation[];
  categories: unknown[];

  layerDirectories: boolean;
  layerGuards: boolean;
  layerCameras: boolean;
  directoryKiosks: DirectoryKiosk[];
  directoryKiosksLoading: boolean;

  // Escape-hatches formerly on window. See state.js in the original.
  hiddenSpaceIds: Set<string>;
  dynamicSpaceIds: Set<string>;
  undergroundSpaces: MappedinSpace[];
  undergroundOrigColors: Record<string, string | null>;
  nonDynOutdoorSpaces: MappedinSpace[] | null;

  setAppMode: (mode: AppMode) => void;
  setVenues: (v: Venue[]) => void;
  setSelectedVenue: (v: Venue | null) => void;
  setHoveredBinKey: (k: string | null) => void;
  setGlobeSearchQuery: (q: string) => void;
  setMapState: (mv: MappedinMapView, md: MappedinMapData) => void;
  clearMapState: () => void;
  setMapLoading: (v: boolean) => void;
  setMapError: (err: string | null) => void;
  setFloors: (f: MappedinFloor[]) => void;
  setOutdoorFloors: (f: MappedinFloor[]) => void;
  setIsMultiBuilding: (v: boolean) => void;
  setActiveFloor: (f: MappedinFloor | null) => void;
  setIsStacked: (v: boolean) => void;
  setFloorGap: (g: number) => void;
  setLocations: (l: MappedinLocation[]) => void;
  setCategories: (c: unknown[]) => void;

  setLayerDirectories: (v: boolean) => void;
  setLayerGuards: (v: boolean) => void;
  setLayerCameras: (v: boolean) => void;
  setDirectoryKiosks: (k: DirectoryKiosk[]) => void;
  setDirectoryKiosksLoading: (v: boolean) => void;
  setAutoBearing: (b: number) => void;

  setLabelsEnabled: (on: boolean) => void;
  setInactiveOpacity: (op: number) => void;
  setShowBounds: (v: boolean) => void;
  setDarkBackground: (dk: boolean) => void;

  setSpaceTracking: (args: {
    hiddenSpaceIds: Set<string>;
    dynamicSpaceIds: Set<string>;
    undergroundSpaces: MappedinSpace[];
    undergroundOrigColors: Record<string, string | null>;
    nonDynOutdoorSpaces: MappedinSpace[] | null;
  }) => void;

  pickVenue: (venue: Venue, opts?: { skipGlobe?: boolean }) => void;
  backToGlobe: () => void;

  applyStackedView: () => void;
  previewAt: (pct: number) => void;
  exitPreview: () => void;
  switchFloor: (fl: MappedinFloor) => void;

  loadPersistedConfig: () => Promise<void>;
}

interface PersistedConfig {
  isStacked: boolean;
  labelsEnabled: boolean;
  darkBackground: boolean;
  showBounds: boolean;
  floorGap: number;
  inactiveOpacity: number;
}

function persistConfig(state: AppState): void {
  const obj: PersistedConfig = {
    isStacked: state.isStacked,
    labelsEnabled: state.labelsEnabled,
    darkBackground: state.darkBackground,
    showBounds: state.showBounds,
    floorGap: state.floorGap,
    inactiveOpacity: state.inactiveOpacity,
  };
  void storage.local.set(CFG_KEY, JSON.stringify(obj));
}

export function bsmtOpacity(elev: number, minElev: number): number {
  if (elev >= 0 || minElev >= 0) return 1.0;
  const depth = -elev;
  const maxDepth = -minElev;
  const ratio = Math.min(1, depth / maxDepth);
  return BSMT_SHALLOW - (BSMT_SHALLOW - BSMT_DEEP) * ratio;
}

export function setUndergroundMono(mapView: MappedinMapView, mono: boolean): void {
  const state = useAppStore.getState();
  const ugSp = state.undergroundSpaces;
  if (!ugSp.length) return;
  const origColors = state.undergroundOrigColors;
  for (const sp of ugSp) {
    const c = mono ? UNDERGROUND_COLOR : (origColors[sp.id] ?? null);
    try {
      mapView.updateState(sp, { color: c });
    } catch {
      // Some spaces may not accept a color update; ignore per original.
    }
  }
}

export function rehideOutdoorNonDynamic(mapView: MappedinMapView): void {
  const ndSp = useAppStore.getState().nonDynOutdoorSpaces;
  if (!ndSp || !ndSp.length) return;
  for (const sp of ndSp) {
    try {
      mapView.updateState(sp, { visible: false, interactive: false });
    } catch {
      // Ignore.
    }
  }
}

function enableMarkersOnAllFloors(mapView: MappedinMapView, floors: MappedinFloor[]): void {
  if (!floors.length) return;
  for (const fl of floors) {
    mapView.updateState(fl, { markers: { enabled: true, occlude: true } });
  }
}

function tweenOpacity(
  mapView: MappedinMapView,
  targets: MappedinFloor[],
  from: number,
  to: number,
  duration: number,
  easing: 'linear' | 'ease-in' | 'ease-out',
): void {
  const ease =
    easing === 'ease-in'
      ? (t: number): number => t * t
      : easing === 'ease-out'
        ? (t: number): number => 1 - (1 - t) * (1 - t)
        : (t: number): number => t;
  const start = performance.now();
  function step(now: number): void {
    const elapsed = now - start;
    const raw = Math.min(1, elapsed / duration);
    const t = ease(raw);
    const op = from + (to - from) * t;
    for (const target of targets) {
      mapView.updateState(target, { geometry: { opacity: op } });
    }
    if (raw < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

interface FloorOrder {
  vis: MappedinFloor[];
  eOrd: Record<number, number>;
  groundOrd: number;
  uElev: number[];
}

function computeFloorOrder(floors: MappedinFloor[]): FloorOrder {
  const vis = floors.slice().sort((a, b) => (a.elevation ?? 0) - (b.elevation ?? 0));
  const uElev: number[] = [];
  const seen: Record<number, boolean> = {};
  for (const f of vis) {
    const e = f.elevation ?? 0;
    if (!seen[e]) {
      seen[e] = true;
      uElev.push(e);
    }
  }
  uElev.sort((a, b) => a - b);
  const eOrd: Record<number, number> = {};
  uElev.forEach((e, i) => {
    eOrd[e] = i;
  });
  const groundOrd = eOrd[0] ?? 0;
  return { vis, eOrd, groundOrd, uElev };
}

interface StackedContext {
  mapView: MappedinMapView;
  order: FloorOrder;
  state: AppState;
  setter: (patch: Partial<AppState>) => void;
  get: () => AppState;
}

/**
 * Mappedin v6 `animateState` only interpolates numeric / color values —
 * boolean fields like `visible`, `labels.enabled`, `markers.enabled`, and
 * `paths.enabled` are rejected frame-by-frame with a loud
 * `[MappedinJS] Value "enabled" could not be animated!` warning, and in
 * some SDK versions prevent the animation from running at all. We
 * therefore apply all discrete / boolean state through `updateState`
 * first, then `animateState` only what's actually animatable
 * (`altitude` and nested numeric `geometry.opacity`).
 */
function animateFloorAltitude(
  mapView: MappedinMapView,
  fl: MappedinFloor,
  altitude: number,
  duration: number,
): void {
  try {
    mapView.animateState(fl, { altitude }, { duration });
  } catch {
    try {
      mapView.updateState(fl, { altitude });
    } catch {
      // Ignore — SDK may be mid-teardown.
    }
  }
}

function applyStackedWithOutdoor(ctx: StackedContext): void {
  const { mapView, order, state: s, setter, get } = ctx;
  const { vis, eOrd, groundOrd } = order;
  const T = 1000;

  for (const fl of vis) {
    const ord = eOrd[fl.elevation ?? 0] ?? 0;
    mapView.updateState(fl, {
      visible: true,
      altitude: (ord - groundOrd) * FACADE_GAP,
      geometry: { visible: true, opacity: 1.0 },
      labels: { enabled: false },
      markers: { enabled: false },
    });
  }
  tweenOpacity(mapView, s.outdoorFloors, 1.0, 0, T * 0.3, 'ease-in');
  for (const od of s.outdoorFloors) {
    mapView.updateState(od, { labels: { enabled: false }, markers: { enabled: false } });
  }
  setTimeout(() => {
    const st = get();
    for (const od of s.outdoorFloors) {
      mapView.updateState(od, { visible: false });
    }
    for (const fl of vis) {
      const ord = eOrd[fl.elevation ?? 0] ?? 0;
      const act = st.activeFloor !== null && fl.id === st.activeFloor.id;
      mapView.updateState(fl, {
        labels: { enabled: st.labelsEnabled && act },
        markers: { enabled: true },
        paths: { enabled: act },
      });
      animateFloorAltitude(mapView, fl, (ord - groundOrd) * st.floorGap, T * 0.7);
    }
    setTimeout(() => enableMarkersOnAllFloors(mapView, vis), T * 0.7 + 100);
  }, T * 0.3);
  setter({ showingFacades: false });
}

function applyStackedNoOutdoor(ctx: StackedContext): void {
  const { mapView, order, state: s, setter } = ctx;
  const { vis, eOrd, groundOrd } = order;
  for (const fl of vis) {
    mapView.updateState(fl, { visible: true, geometry: { visible: true } });
  }
  for (const fl of vis) {
    const ord = eOrd[fl.elevation ?? 0] ?? 0;
    const act = s.activeFloor !== null && fl.id === s.activeFloor.id;
    mapView.updateState(fl, {
      labels: { enabled: s.labelsEnabled && act },
      markers: { enabled: true },
      paths: { enabled: act },
    });
    animateFloorAltitude(mapView, fl, (ord - groundOrd) * s.floorGap, 600);
  }
  setter({ showingFacades: false });
  setTimeout(() => enableMarkersOnAllFloors(mapView, vis), 700);
}

function applyFlatWithOutdoor(ctx: StackedContext): void {
  const { mapView, order, state: s, setter } = ctx;
  const { vis, eOrd, groundOrd } = order;
  const T = 1000;
  for (const fl of vis) {
    const ord = eOrd[fl.elevation ?? 0] ?? 0;
    if ((fl.elevation ?? 0) < 0) continue;
    mapView.updateState(fl, {
      visible: true,
      geometry: { visible: true, opacity: 1.0 },
      labels: { enabled: false },
      markers: { enabled: false },
      paths: { enabled: false },
    });
    animateFloorAltitude(mapView, fl, (ord - groundOrd) * FACADE_GAP, T * 0.6);
  }
  setTimeout(() => {
    for (const od of s.outdoorFloors) {
      mapView.updateState(od, {
        visible: true,
        altitude: 0,
        geometry: { visible: true, opacity: 0 },
        labels: { enabled: s.labelsEnabled },
        markers: { enabled: true },
      });
    }
    rehideOutdoorNonDynamic(mapView);
    tweenOpacity(mapView, s.outdoorFloors, 0, 1.0, T * 0.3, 'ease-out');
  }, T * 0.4);
  setTimeout(() => {
    setter({ showingFacades: true });
    rehideOutdoorNonDynamic(mapView);
  }, T * 0.7);
}

function applyFlatNoOutdoor(ctx: StackedContext): void {
  const { mapView, order, setter } = ctx;
  const { vis, eOrd, groundOrd } = order;
  for (const fl of vis) {
    const ord = eOrd[fl.elevation ?? 0] ?? 0;
    if ((fl.elevation ?? 0) < 0) continue;
    mapView.updateState(fl, {
      visible: true,
      geometry: { visible: true, opacity: 1.0 },
      labels: { enabled: false },
      markers: { enabled: false },
      paths: { enabled: false },
    });
    animateFloorAltitude(mapView, fl, (ord - groundOrd) * FACADE_GAP, 600);
  }
  setter({ showingFacades: true });
}

function applyPreviewOutdoor(
  mapView: MappedinMapView,
  outdoorFloors: MappedinFloor[],
  p: number,
  labelsEnabled: boolean,
): void {
  let odOp = 0;
  if (p >= 0.7) odOp = 1.0;
  else if (p > 0.4) {
    const t2 = (p - 0.4) / 0.3;
    odOp = 1 - (1 - t2) * (1 - t2);
  }
  const odVis = odOp > 0.001;
  for (const od of outdoorFloors) {
    mapView.updateState(od, {
      visible: odVis,
      altitude: 0,
      geometry: { visible: odVis, opacity: Math.max(0, odOp) },
      labels: { enabled: odVis && odOp > 0.5 && labelsEnabled },
      markers: { enabled: odVis && odOp > 0.5 },
    });
  }
  if (odVis) rehideOutdoorNonDynamic(mapView);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function coercePersistedConfig(raw: unknown): Partial<PersistedConfig> | null {
  let parsed: unknown = null;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  } else if (isRecord(raw)) {
    parsed = raw;
  }
  if (!isRecord(parsed)) return null;
  const out: Partial<PersistedConfig> = {};
  if (typeof parsed.isStacked === 'boolean') out.isStacked = parsed.isStacked;
  if (typeof parsed.labelsEnabled === 'boolean') out.labelsEnabled = parsed.labelsEnabled;
  if (typeof parsed.darkBackground === 'boolean') out.darkBackground = parsed.darkBackground;
  if (typeof parsed.showBounds === 'boolean') out.showBounds = parsed.showBounds;
  if (typeof parsed.floorGap === 'number') out.floorGap = parsed.floorGap;
  if (typeof parsed.inactiveOpacity === 'number') out.inactiveOpacity = parsed.inactiveOpacity;
  return out;
}

export const useAppStore = create<AppState>((set, get) => ({
  appMode: 'globe',
  venues: [],
  venuesLoading: true,
  selectedVenue: null,
  hoveredBinKey: null,
  showVenueMap: false,
  globeSearchQuery: '',

  mapView: null,
  mapData: null,
  mapLoading: false,
  mapError: null,

  floors: [],
  outdoorFloors: [],
  activeFloor: null,
  isStacked: true,
  floorGap: 40,
  isMultiBuilding: false,
  showingFacades: false,
  animPreview: null,

  pitch: 60,
  bearing: 0,
  autoBearing: 0,

  labelsEnabled: false,
  inactiveOpacity: 1.0,
  darkBackground: true,
  showBounds: false,

  locations: [],
  categories: [],

  layerDirectories: false,
  layerGuards: false,
  layerCameras: false,
  directoryKiosks: [],
  directoryKiosksLoading: false,

  hiddenSpaceIds: new Set<string>(),
  dynamicSpaceIds: new Set<string>(),
  undergroundSpaces: [],
  undergroundOrigColors: {},
  nonDynOutdoorSpaces: null,

  setAppMode: (mode) => set({ appMode: mode }),
  setVenues: (v) => set({ venues: v, venuesLoading: false }),
  setSelectedVenue: (v) => set({ selectedVenue: v }),
  setHoveredBinKey: (k) => set({ hoveredBinKey: k }),
  setGlobeSearchQuery: (q) => set({ globeSearchQuery: q }),
  setMapState: (mv, md) => set({ mapView: mv, mapData: md, mapLoading: false, mapError: null }),
  clearMapState: () => set({ mapView: null, mapData: null }),
  setMapLoading: (v) => set({ mapLoading: v }),
  setMapError: (err) => set({ mapError: err, mapLoading: false }),
  setFloors: (f) => {
    const elevs: number[] = [];
    const seen: Record<number, boolean> = {};
    for (const fl of f) {
      const e = fl.elevation ?? 0;
      if (!seen[e]) {
        seen[e] = true;
        elevs.push(e);
      }
    }
    const levels = elevs.length;
    const autoGap = levels <= 1 ? 40 : Math.round(Math.max(25, Math.min(90, 200 / (levels - 1))));
    const currentGap = get().floorGap;
    const hasPersistedGap = currentGap !== 40 && currentGap !== autoGap;
    set({ floors: f, floorGap: hasPersistedGap ? currentGap : autoGap });
  },
  setOutdoorFloors: (f) => set({ outdoorFloors: f }),
  setIsMultiBuilding: (v) => set({ isMultiBuilding: v }),
  setActiveFloor: (f) => set({ activeFloor: f }),
  setIsStacked: (v) => {
    set({ isStacked: v });
    persistConfig(get());
  },
  setFloorGap: (g) => {
    set({ floorGap: g });
    persistConfig(get());
  },
  setLocations: (l) => set({ locations: l }),
  setCategories: (c) => set({ categories: c }),

  setLayerDirectories: (v) => set({ layerDirectories: v }),
  setLayerGuards: (v) => set({ layerGuards: v }),
  setLayerCameras: (v) => set({ layerCameras: v }),
  setDirectoryKiosks: (k) => set({ directoryKiosks: k, directoryKiosksLoading: false }),
  setDirectoryKiosksLoading: (v) => set({ directoryKiosksLoading: v }),
  setAutoBearing: (b) => set({ autoBearing: b, bearing: b }),

  setLabelsEnabled: (on) => {
    set({ labelsEnabled: on });
    persistConfig(get());
    const s = get();
    if (!s.mapView || !s.floors.length) return;
    for (const fl of s.floors) {
      const act = s.activeFloor && fl.id === s.activeFloor.id;
      s.mapView.updateState(fl, { labels: { enabled: on && (act || !s.isStacked) } });
    }
  },
  setInactiveOpacity: (op) => {
    set({ inactiveOpacity: op });
    persistConfig(get());
    const s = get();
    if (!s.mapView || !s.floors.length || !s.isStacked) return;
    for (const fl of s.floors) {
      if (!(s.activeFloor && fl.id === s.activeFloor.id)) {
        s.mapView.updateState(fl, { geometry: { opacity: op } });
      }
    }
  },
  setShowBounds: (v) => {
    set({ showBounds: v });
    persistConfig(get());
  },

  setDarkBackground: (dk) => {
    set({ darkBackground: dk });
    persistConfig(get());
    const mv = get().mapView;
    if (mv) mv.updateGlobalState({ backgroundColor: dk ? '#0e1320' : '#1a1f2e' });
  },

  setSpaceTracking: ({
    hiddenSpaceIds,
    dynamicSpaceIds,
    undergroundSpaces,
    undergroundOrigColors,
    nonDynOutdoorSpaces,
  }) =>
    set({
      hiddenSpaceIds,
      dynamicSpaceIds,
      undergroundSpaces,
      undergroundOrigColors,
      nonDynOutdoorSpaces,
    }),

  pickVenue: (venue, _opts) => {
    set({
      selectedVenue: venue,
      appMode: 'venue',
      showVenueMap: false,
    });
    setTimeout(() => set({ showVenueMap: true }), 100);
    void storage.set('selected-venue-id', venue.id);
  },

  backToGlobe: () => {
    set({
      appMode: 'globe',
      selectedVenue: null,
      showVenueMap: false,
      mapView: null,
      mapData: null,
      mapLoading: false,
      mapError: null,
      floors: [],
      outdoorFloors: [],
      activeFloor: null,
      isMultiBuilding: false,
      showingFacades: false,
      layerDirectories: false,
      layerGuards: false,
      layerCameras: false,
      directoryKiosks: [],
      directoryKiosksLoading: false,
    });
    void storage.delete('selected-venue-id');
  },

  applyStackedView: () => {
    const s = get();
    const mapView = s.mapView;
    if (!mapView || !s.floors.length) return;

    const order = computeFloorOrder(s.floors);
    const hasOutdoor = s.isMultiBuilding && s.outdoorFloors.length > 0;
    const ctx: StackedContext = {
      mapView,
      order,
      state: s,
      setter: (patch) => set(patch),
      get,
    };

    if (s.isStacked) {
      set({ showingFacades: false });
      for (const fl of order.vis) {
        mapView.updateState(fl, { visible: true, geometry: { visible: true, opacity: 1.0 } });
      }
      if (hasOutdoor) applyStackedWithOutdoor(ctx);
      else applyStackedNoOutdoor(ctx);
    } else {
      for (const fl of order.vis) {
        if ((fl.elevation ?? 0) < 0) {
          mapView.updateState(fl, { visible: false, geometry: { visible: false } });
        }
      }
      if (hasOutdoor) applyFlatWithOutdoor(ctx);
      else applyFlatNoOutdoor(ctx);
    }
  },

  previewAt: (pct) => {
    const s = get();
    if (!s.mapView || !s.floors.length) return;
    set({ animPreview: pct });
    const { vis, eOrd, groundOrd, uElev } = computeFloorOrder(s.floors);
    const hasOD = s.isMultiBuilding && s.outdoorFloors.length > 0;
    const p = pct / 100;
    for (const fl of vis) {
      const ord = eOrd[fl.elevation ?? 0] ?? 0;
      const stackedAlt = (ord - groundOrd) * s.floorGap;
      const facadeAlt = (ord - groundOrd) * FACADE_GAP;
      const alt = stackedAlt + (facadeAlt - stackedAlt) * p;
      const showM = hasOD ? p < 0.3 : p < 0.5;
      const bTarget = bsmtOpacity(fl.elevation ?? 0, uElev[0] ?? 0);
      const bsmtP = 1.0 + (bTarget - 1.0) * p;
      s.mapView.updateState(fl, {
        visible: true,
        altitude: alt,
        geometry: { visible: true, opacity: bsmtP },
        labels: { enabled: showM && s.labelsEnabled },
        markers: { enabled: showM },
      });
    }
    if (hasOD) applyPreviewOutdoor(s.mapView, s.outdoorFloors, p, s.labelsEnabled);
  },

  exitPreview: () => {
    set({ animPreview: null });
    get().applyStackedView();
  },

  switchFloor: (fl) => {
    const s = get();
    if (!s.mapView || !fl) return;
    set({ activeFloor: fl });
    s.mapView.setFloor(fl);
    if (s.isStacked) enableMarkersOnAllFloors(s.mapView, s.floors);
  },

  loadPersistedConfig: async () => {
    try {
      const raw = await storage.local.get(CFG_KEY);
      const saved = coercePersistedConfig(raw);
      if (!saved) return;
      set(saved);
    } catch {
      // Ignore missing/corrupt persisted config.
    }
  },
}));

// Load persisted config at import time so components read the right values.
void useAppStore.getState().loadPersistedConfig();
